import { useState } from 'react';
import { useSubscriptionContext } from '../../../contexts/SubscriptionContext';
import { useUserContext } from '../../../contexts/UserContext';
import { getStatusBadge, formatDate, formatCurrency, getDaysUntil } from '../../../utils/subscriptionUtils';
import { MODULE_CONFIG, MODULE_KEYS_ORDERED } from '../../../config/subscriptionConfig';
import { Btn } from '../../ui';

export const CurrentPlanCard = () => {
    const { accountStatus, isTrialActive, isCoreActive, monthlySummary, isModuleActive, isModuleLocked } = useSubscriptionContext();
    const { company } = useUserContext();
    const [showManageDropdown, setShowManageDropdown] = useState(false);

    const companyData = company as Record<string, unknown> | null;
    const statusBadge = getStatusBadge(accountStatus);
    const nextBillingDate = companyData?.nextBillingDate as string | null;
    const trialEndDate = companyData?.trialEndDate as string | null;
    const coreMonthlyRate = (companyData?.coreMonthlyRate as number) || 99;

    // Plan label
    const planLabel = isTrialActive ? 'Free Demo' : 'Core Program';
    const planDetail = isTrialActive
        ? 'All modules unlocked during demo'
        : `$${coreMonthlyRate}/mo + ${monthlySummary.addOns.length} add-on${monthlySummary.addOns.length !== 1 ? 's' : ''}`;

    // Next date display
    const dateLabel = isTrialActive ? 'EXPIRES' : 'NEXT BILLING';
    const dateValue = isTrialActive ? trialEndDate : nextBillingDate;
    const daysUntil = dateValue ? getDaysUntil(dateValue) : null;

    return (
        <div className={`current-plan-card ${accountStatus === 'past-due' ? 'current-plan-card--past-due' : ''}`}>
            <div className="current-plan-card-header">
                <div className="current-plan-card-header-left">
                    <span className="current-plan-label">CURRENT PLAN</span>
                    <span className={`current-plan-status-badge badge-${statusBadge.variant}`}>
                        ● {statusBadge.label}
                    </span>
                </div>
                <div className="current-plan-card-header-right">
                    {isCoreActive && (
                        <div className="current-plan-manage-wrapper">
                            <Btn
                                variant="secondary-outlined"
                                buttonSize="sm"
                                buttonText="Manage Plan"
                                buttonOnClick={() => setShowManageDropdown(!showManageDropdown)}
                            />
                            {showManageDropdown && (
                                <div className="current-plan-dropdown">
                                    <button className="current-plan-dropdown-item">
                                        <span>🔄</span> Update payment method
                                    </button>
                                    <button className="current-plan-dropdown-item">
                                        <span>✉️</span> Change billing email
                                    </button>
                                    <button className="current-plan-dropdown-item">
                                        <span>⬇️</span> Download all invoices (CSV)
                                    </button>
                                    <div className="current-plan-dropdown-divider" />
                                    <button className="current-plan-dropdown-item current-plan-dropdown-item--muted">
                                        <span>⏸️</span> Pause subscription <span className="current-plan-dropdown-tag">Contact admin</span>
                                    </button>
                                    <button className="current-plan-dropdown-item current-plan-dropdown-item--danger">
                                        <span>⊘</span> Cancel subscription
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                    {isTrialActive && (
                        <Btn
                            variant="primary"
                            buttonSize="sm"
                            buttonText="Subscribe to Core Program"
                            buttonOnClick={() => {/* TODO: open checkout modal */ }}
                        />
                    )}
                    {accountStatus === 'past-due' && (
                        <Btn
                            variant="danger"
                            buttonSize="sm"
                            buttonText="Update Card"
                            buttonOnClick={() => {/* TODO: open payment modal */ }}
                        />
                    )}
                </div>
            </div>

            {/* Plan Details Row */}
            <div className="current-plan-details">
                <div className="current-plan-detail-item">
                    <span className="current-plan-detail-label">PLAN</span>
                    <span className="current-plan-detail-value">{planLabel}</span>
                    <span className="current-plan-detail-sub">{planDetail}</span>
                </div>
                <div className="current-plan-detail-item">
                    <span className="current-plan-detail-label">{dateLabel}</span>
                    <span className="current-plan-detail-value">{formatDate(dateValue)}</span>
                    {daysUntil !== null && (
                        <span className="current-plan-detail-sub">in {daysUntil} days</span>
                    )}
                </div>
                <div className="current-plan-detail-item">
                    <span className="current-plan-detail-label">USERS</span>
                    <span className="current-plan-detail-value">{isTrialActive ? '—' : 'No limit'}</span>
                    <span className="current-plan-detail-sub">{isTrialActive ? 'No limits during demo' : 'No seat limit'}</span>
                </div>
                {isCoreActive && (
                    <div className="current-plan-detail-item">
                        <span className="current-plan-detail-label">MONTHLY TOTAL</span>
                        <span className="current-plan-detail-value">{formatCurrency(monthlySummary.total)}</span>
                        <span className="current-plan-detail-sub">
                            {formatCurrency(coreMonthlyRate)} core + {formatCurrency(monthlySummary.total - coreMonthlyRate)} add-ons
                        </span>
                    </div>
                )}
            </div>

            {/* Active Modules Pills */}
            <div className="current-plan-modules">
                <span className="current-plan-modules-label">ACTIVE MODULES:</span>
                <div className="current-plan-modules-pills">
                    {/* Core modules always active */}
                    {['Inspections', 'Equipment', 'Daily Briefing', 'Reports'].map((name) => (
                        <span key={name} className="current-plan-pill current-plan-pill--active">
                            ✓ {name}
                        </span>
                    ))}
                    {/* Add-on modules */}
                    {MODULE_KEYS_ORDERED.map((key) => {
                        const config = MODULE_CONFIG[key];
                        const active = isModuleActive(key);
                        const locked = isModuleLocked(key);
                        return (
                            <span
                                key={key}
                                className={`current-plan-pill ${active ? 'current-plan-pill--active' : 'current-plan-pill--locked'}`}
                            >
                                {active ? '✓' : '🔒'} {config.shortName}{locked ? ' (locked)' : ''}
                            </span>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};
